import { Component, Inject, Injector, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, Product, ProductService, moneyMaskOutput } from 'lib-trend-core';
import { finalize, takeUntil } from 'rxjs';

@Component({
    selector: 'app-product-form-modal',
    templateUrl: './product-form-modal.component.html',
    styleUrl: './product-form-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProductFormModalComponent extends AbstractComponent {
  /** ngx-mask hands the money inputs a string; the model keeps a number. */
  protected readonly moneyMaskOutput = moneyMaskOutput;

  public product: Product;
  private products: Product[] = [];

  constructor(
    injector: Injector,
    public dialogRef: MatDialogRef<ProductFormModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Product,
    private productService: ProductService,
  ) {
    super(injector);
    this.setupForm();
  }

  ngOnInit(): void {
    this.isNew = !this.data;
    this.loadProducts();
  }

  private setupForm(): void {
    this.formGroup = this.formBuilder.group({
      name: [this.data ? this.data.name : '', [Validators.required]],
      description: [this.data ? this.data.description : '', []],
      price: [this.data ? this.data.price : 0, [Validators.required, Validators.min(0)]],
      code: [this.data ? this.data.code : '', [Validators.required]],
    });

    this.formGroup.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (!this.product) {
          this.product = {} as Product;
        }
        Object.assign(this.product, value);
      });

    this.watchCodeChanges();
  }

  private watchCodeChanges(): void {
    this.formGroup.get('code')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((code: string) => this.verifyUniqueCode(this.products, code));
  }

  private loadProducts(): void {
    this.productService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (products: Product[]) => {
          this.products = products ?? [];
          this.verifyUniqueCode(this.products, this.formGroup.get('code')?.value);
        },
        error: (error) => this.alertService.error(error.error?.message || this.t('kanban.productFormModal.loadError')),
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    this.verifyUniqueCode(this.products, this.formGroup.get('code')?.value);
    if (!this.formGroup.valid) {
      this.validateAllFormFields(this.formGroup);
      return;
    };

    this.loading = true;

    this.product = {
      ...(this.product ?? {}),
      ...this.formGroup.getRawValue(),
      company: super.getCompanyCurrentUser(),
    };

    const saveObservable = this.isNew
      ? this.productService.create(this.product)
      : this.productService.update(this.data._id!, this.product);

    saveObservable
      .pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe({
        next: (product: Product) => {
          this.alertService.success(this.t(this.isNew ? 'kanban.productFormModal.createSuccess' : 'kanban.productFormModal.updateSuccess'));
          this.dialogRef.close(product);
        },
        error: (error) => {
          this.alertService.error(error.error.message || this.t('kanban.productFormModal.saveError'));
          this.loading = false;
        }
      });
  }


  verifyUniqueCode(products: Product[], code: string): void {
    const control = this.formGroup?.get('code');
    const normalizedCode = (code ?? '').trim();

    if (!normalizedCode) {
      if (control.hasError('codeExists')) {
        const { codeExists, ...errors } = control.errors ?? {};
        control.setErrors(Object.keys(errors).length ? errors : null);
      }
      return;
    }

    const exists = products?.some(
      (p) => (p.code ?? '').trim() === normalizedCode && (!this.data?._id || p._id !== this.data._id)
    );

    if (exists) {
      control.setErrors({ ...(control.errors ?? {}), codeExists: true });
    } else if (control.hasError('codeExists')) {
      const { codeExists, ...errors } = control.errors ?? {};
      control.setErrors(Object.keys(errors).length ? errors : null);
    }
  }
}
